import { useState, useEffect } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/store/authStore'
import { useHandoverUnreadStore } from '@/store/handoverUnreadStore'
import { apiService } from '@/services/api'
import {
  LayoutDashboard,
  Users,
  CreditCard,
  ArrowUpRight,
  TrendingUp,
  BarChart3,
  MessageSquare,
  HelpCircle,
  Megaphone,
  UserCheck,
  Coins,
  Zap,
  Calendar,
  Mail,
  BookOpen,
  X,
  Crown,
  FileText,
  Target,
  Settings,
  DollarSign,
  Activity,
  ScrollText,
  Eye,
  Trophy,
  Award,
  ChevronDown,
  Briefcase,
  ClipboardList,
  ClipboardCheck,
  Clock,
  Users2,
  Globe,
  Wrench,
  Search,
  Share2,
  ShieldCheck,
  Wallet,
  LogOut,
  Maximize2, Gift,
  MinusCircle, MonitorSmartphone
} from 'lucide-react'

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
}

interface MenuItem {
  titleKey: string
  icon: any
  path: string
  color: string
}

interface MenuGroup {
  id: string
  titleKey: string
  icon: any
  color: string
  children: MenuItem[]
}

type MenuEntry = MenuItem | MenuGroup

const isGroup = (entry: MenuEntry): entry is MenuGroup => 'children' in entry

const STORAGE_KEY = 'admin_sidebar_collapsed'

const menuStructure: MenuEntry[] = [
  { titleKey: 'sidebar:dashboard', icon: LayoutDashboard, path: '/admin/dashboard', color: 'text-primary-400' },

  {
    id: 'operations',
    titleKey: 'sidebar:operations',
    icon: Briefcase,
    color: 'text-blue-400',
    children: [
      { titleKey: 'sidebar:handover', icon: ClipboardList, path: '/admin/handover', color: 'text-indigo-400' },
      { titleKey: 'sidebar:promotions', icon: Gift, path: '/admin/promotions', color: 'text-pink-400' },
      { titleKey: 'sidebar:userManagement', icon: Users, path: '/admin/users', color: 'text-blue-400' },
      { titleKey: 'sidebar:demoUsers', icon: Users, path: '/admin/demo-users', color: 'text-emerald-400' },
      { titleKey: 'sidebar:depositsWithdrawals', icon: CreditCard, path: '/admin/deposits-withdrawals-management', color: 'text-green-400' },
      { titleKey: 'sidebar:demoDeposits', icon: CreditCard, path: '/admin/demo-deposits', color: 'text-emerald-400' },
      { titleKey: 'sidebar:positionStatus', icon: Target, path: '/admin/positions', color: 'text-lime-400' },
      { titleKey: 'sidebar:chatManagement', icon: MessageSquare, path: '/admin/chat', color: 'text-pink-400' },
      { titleKey: 'sidebar:inquiryManagement', icon: HelpCircle, path: '/admin/inquiries', color: 'text-cyan-400' },
      { titleKey: 'sidebar:consultations', icon: FileText, path: '/admin/consultations', color: 'text-teal-400' },
      { titleKey: 'sidebar:ipBlocks', icon: ShieldCheck, path: '/admin/ip-blocks', color: 'text-red-400' },
      { titleKey: 'sidebar:deviceBlocks', icon: ShieldCheck, path: '/admin/device-blocks', color: 'text-red-400' },
    ]
  },

  {
    id: 'history',
    titleKey: 'sidebar:historyStats',
    icon: ClipboardList,
    color: 'text-cyan-400',
    children: [
      { titleKey: 'sidebar:depositHistory', icon: CreditCard, path: '/admin/deposits-history', color: 'text-green-400' },
      { titleKey: 'sidebar:withdrawalHistory', icon: ArrowUpRight, path: '/admin/withdrawals-history', color: 'text-red-400' },
      { titleKey: 'sidebar:manualRequests', icon: ClipboardCheck, path: '/admin/manual-requests', color: 'text-indigo-400' },
      { titleKey: 'sidebar:balanceTracking', icon: Search, path: '/admin/balance-tracking', color: 'text-cyan-400' },
      { titleKey: 'sidebar:stakingHistory', icon: Coins, path: '/admin/staking-history', color: 'text-emerald-400' },
      { titleKey: 'sidebar:positionHistory', icon: TrendingUp, path: '/admin/position-history', color: 'text-lime-400' },
      { titleKey: 'sidebar:messageHistory', icon: Mail, path: '/admin/message-history', color: 'text-purple-400' },
      { titleKey: 'sidebar:moneyLog', icon: DollarSign, path: '/admin/money-log', color: 'text-purple-400' },
      { titleKey: 'sidebar:partnerBonus', icon: Gift, path: '/admin/partner-bonus', color: 'text-fuchsia-400' },
      { titleKey: 'sidebar:accessLogs', icon: Activity, path: '/admin/access-logs', color: 'text-indigo-400' },
      { titleKey: 'sidebar:eventLogs', icon: ScrollText, path: '/admin/event-logs', color: 'text-amber-400' },
      { titleKey: 'sidebar:htsLogs', icon: ScrollText, path: '/admin/hts-logs', color: 'text-cyan-400' },
      { titleKey: 'sidebar:htsAppUsage', icon: MonitorSmartphone, path: '/admin/hts-app-usage', color: 'text-cyan-400' },
      { titleKey: 'sidebar:siteAnalytics', icon: Eye, path: '/admin/page-analytics', color: 'text-cyan-400' },
      { titleKey: 'sidebar:dailyStatistics', icon: BarChart3, path: '/admin/daily-statistics', color: 'text-cyan-400' },
      { titleKey: 'sidebar:userStatistics', icon: Users, path: '/admin/user-statistics', color: 'text-blue-400' },
    ]
  },

  {
    id: 'content',
    titleKey: 'sidebar:contentManagement',
    icon: Globe,
    color: 'text-orange-400',
    children: [
      { titleKey: 'sidebar:marketManagement', icon: BarChart3, path: '/admin/markets', color: 'text-yellow-400' },
      { titleKey: 'sidebar:notices', icon: Megaphone, path: '/admin/notices', color: 'text-orange-400' },
      { titleKey: 'sidebar:noticeConsents', icon: ShieldCheck, path: '/admin/notice-consents', color: 'text-red-400' },
      { titleKey: 'sidebar:templateManagement', icon: FileText, path: '/admin/templates', color: 'text-blue-300' },
    ]
  },

  {
    id: 'partner',
    titleKey: 'sidebar:partner',
    icon: Users2,
    color: 'text-teal-400',
    children: [
      { titleKey: 'sidebar:partnerManagement', icon: UserCheck, path: '/admin/partners', color: 'text-teal-400' },
      { titleKey: 'sidebar:partnerFunds', icon: Wallet, path: '/admin/partner-funds', color: 'text-emerald-400' },
      { titleKey: 'sidebar:partnerWithdrawals', icon: ArrowUpRight, path: '/admin/partner-withdrawals', color: 'text-orange-400' },
      { titleKey: 'sidebar:partnerDeductionLogs', icon: MinusCircle, path: '/admin/partner-deduction-logs', color: 'text-amber-400' },
      { titleKey: 'sidebar:partnerInquiries', icon: HelpCircle, path: '/admin/partner-inquiries', color: 'text-sky-400' },
    ]
  },

  {
    id: 'service',
    titleKey: 'sidebar:serviceManagement',
    icon: Wrench,
    color: 'text-violet-400',
    children: [
      { titleKey: 'sidebar:staking', icon: Coins, path: '/admin/staking', color: 'text-emerald-400' },
      { titleKey: 'sidebar:popupAds', icon: Zap, path: '/admin/popup-ads', color: 'text-violet-400' },
      { titleKey: 'sidebar:eventManagement', icon: Calendar, path: '/admin/events', color: 'text-indigo-400' },
      { titleKey: 'sidebar:competitionManagement', icon: Award, path: '/admin/competitions', color: 'text-amber-400' },
      { titleKey: 'sidebar:messageManagement', icon: Mail, path: '/admin/messages', color: 'text-purple-400' },
      { titleKey: 'sidebar:membershipTiers', icon: Crown, path: '/admin/membership-tiers', color: 'text-yellow-500' },
    ]
  },

  {
    id: 'settings',
    titleKey: 'sidebar:settings',
    icon: Settings,
    color: 'text-slate-400',
    children: [
      { titleKey: 'sidebar:faqManagement', icon: BookOpen, path: '/admin/faqs', color: 'text-amber-400' },
      { titleKey: 'sidebar:termsManagement', icon: FileText, path: '/admin/terms', color: 'text-slate-400' },
      { titleKey: 'sidebar:missionStatistics', icon: BarChart3, path: '/admin/mission-statistics', color: 'text-yellow-400' },
      { titleKey: 'sidebar:missionSettings', icon: Trophy, path: '/admin/missions', color: 'text-yellow-400' },
      { titleKey: 'sidebar:exchangeSettings', icon: Settings, path: '/admin/trading-settings', color: 'text-indigo-400' },
      { titleKey: 'sidebar:serviceBlock', icon: Clock, path: '/admin/service-block', color: 'text-indigo-400' },
      { titleKey: 'sidebar:shareCardSettings', icon: Share2, path: '/admin/share-card-settings', color: 'text-cyan-400' },
    ]
  },
]

/* 실시간 접속 회원 미니 목록 (2026-08-01 사장님) — 사이드바 하단 고정, 데스크톱 전용.
 * 회원 프론트 5초 폴링이 하트비트 — 브라우저 닫힘/폰 꺼짐/앱 이탈 시 15초 내 사라짐. */
// 미니 목록용 유지시간 축약: 방금 / N분 / N시간M분
const miniDuration = (s: number) => {
  if (!s || s < 60) return '방금'
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}분`
  return `${Math.floor(m / 60)}시간${m % 60 ? `${m % 60}분` : ''}`
}

type MiniMember = { user_id: number; name: string; nickname: string; dur_min: number }

const OnlineMembersMini = () => {
  const navigate = useNavigate()
  const [members, setMembers] = useState<MiniMember[]>([])

  useEffect(() => {
    // 데스크톱에서만 폴링 (모바일 서랍 사이드바에선 미표시 — 사장님 지시)
    if (window.innerWidth < 1024) return
    let alive = true
    const load = async () => {
      if (document.hidden) return   // 관리자 탭이 백그라운드면 폴링 정지 (렉/부하 방지)
      try {
        const response = await apiService.get('/api/v1/admin/online-members')
        const raw = ((response as any)?.data || []) as any[]
        // 분 단위로 축약 — 표시가 실제로 바뀔 때만 setState (매 주기 불필요 리렌더 방지)
        const next: MiniMember[] = raw.map(m => ({
          user_id: m.user_id, name: m.name, nickname: m.nickname,
          dur_min: Math.floor((m.duration_sec || 0) / 60),
        }))
        if (alive) setMembers(prev =>
          JSON.stringify(prev) === JSON.stringify(next) ? prev : next)
      } catch { /* 다음 주기 재시도 */ }
    }
    load()
    const iv = window.setInterval(load, 8000)
    const onVisible = () => { if (!document.hidden) load() }
    document.addEventListener('visibilitychange', onVisible)
    return () => { alive = false; window.clearInterval(iv); document.removeEventListener('visibilitychange', onVisible) }
  }, [])

  return (
    <div className="hidden lg:flex flex-col border-t border-white/10 flex-shrink-0">
      <div className="flex items-center justify-between px-3 py-2 flex-shrink-0">
        <div className="flex items-center space-x-2">
          {/* 상시 애니메이션 제거(사이드바는 항상 떠있어 렉 요인) — 정적 초록 점 */}
          <span className="inline-flex rounded-full h-2 w-2 bg-green-500" />
          <span className="text-[11px] font-semibold uppercase tracking-wider text-dark-300">
            실시간 접속 <span className="text-green-400">{members.length}</span>
          </span>
        </div>
        <button
          onClick={() => navigate('/admin/online-members')}
          title="자세히 보기"
          className="p-1 rounded hover:bg-white/10 text-dark-400 hover:text-white transition-colors"
        >
          <Maximize2 className="w-3.5 h-3.5" />
        </button>
      </div>
      {/* 4명 높이 고정 — 4명 초과 시 스크롤로 전부 확인 (2026-08-01 사장님) */}
      <div className="overflow-y-auto max-h-[110px] px-2 pb-2 space-y-0.5">
        {members.length === 0 ? (
          <p className="text-[11px] text-dark-500 px-1 pb-1">접속 중인 회원 없음</p>
        ) : (
          members.map(m => (
            <button
              key={m.user_id}
              onClick={() => navigate(`/admin/users/${m.user_id}`)}
              className="w-full flex items-center px-2 py-1 rounded text-xs hover:bg-white/5 transition-colors"
            >
              <span className="text-dark-200 truncate flex-1 text-left hover:text-white">
                {m.name} <span className="text-dark-400">({m.nickname})</span>
              </span>
              <span className="text-[10px] text-green-400/80 flex-shrink-0 ml-1.5 tabular-nums">{miniDuration(m.dur_min * 60)}</span>
            </button>
          ))
        )}
      </div>
    </div>
  )
}

const Sidebar = ({ isOpen, onClose }: SidebarProps) => {
  const { t } = useTranslation('sidebar')
  const unreadHandover = useHandoverUnreadStore(s => s.count)
  const location = useLocation()
  const { logout } = useAuthStore()

  // localStorage에서 접힘 상태 복원
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      return saved ? JSON.parse(saved) : {}
    } catch {
      return {}
    }
  })

  // collapsed 변경 시 localStorage 저장
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(collapsed))
  }, [collapsed])

  const toggleGroup = (id: string) => {
    setCollapsed(prev => ({ ...prev, [id]: !prev[id] }))
  }

  // 그룹 내 현재 active child 있는지 확인
  const hasActiveChild = (group: MenuGroup) => {
    return group.children.some(child => location.pathname === child.path || location.pathname.startsWith(child.path + '/'))
  }

  const renderMenuItem = (item: MenuItem) => (
    <NavLink
      key={item.path}
      to={item.path}
      className={({ isActive }) =>
        `flex items-center space-x-2 rounded-lg transition-all duration-200 group px-3 py-2 ${
          isActive
            ? 'bg-white/10 text-white shadow-glass-sm'
            : 'text-dark-300 hover:bg-white/5 hover:text-white'
        }`
      }
      onClick={() => {
        if (window.innerWidth < 1024) onClose()
      }}
    >
      {({ isActive }) => (
        <>
          <div className={`p-1.5 rounded-lg transition-all duration-200 ${
            isActive ? 'bg-white/10 shadow-glass-sm' : 'group-hover:bg-white/5'
          }`}>
            <item.icon className={`w-4 h-4 transition-colors duration-200 ${
              isActive ? item.color : 'text-dark-400 group-hover:text-dark-200'
            }`} />
          </div>
          <span className="text-sm font-medium">{t(item.titleKey)}</span>
          {item.path === '/admin/handover' && unreadHandover > 0 && (
            <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-indigo-500 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
              {unreadHandover > 99 ? '99+' : unreadHandover}
            </span>
          )}
          {isActive && !(item.path === '/admin/handover' && unreadHandover > 0) && <div className="ml-auto w-1.5 h-1.5 bg-primary-500 rounded-full flex-shrink-0" />}
        </>
      )}
    </NavLink>
  )

  const renderGroup = (group: MenuGroup) => {
    const isCollapsed = collapsed[group.id] ?? false
    const isActive = hasActiveChild(group)

    return (
      <div key={group.id} className="mt-3 first:mt-0">
        {/* 그룹 헤더 - 섹션 라벨 스타일 */}
        <button
          onClick={() => toggleGroup(group.id)}
          className="w-full flex items-center px-3 py-1.5 transition-all duration-200 group"
        >
          <group.icon className={`w-3.5 h-3.5 mr-1.5 transition-colors duration-200 ${
            isActive ? group.color : 'text-dark-500'
          }`} />
          <span className={`text-[11px] font-semibold uppercase tracking-wider flex-1 text-left ${
            isActive ? 'text-dark-200' : 'text-dark-500'
          }`}>
            {t(group.titleKey)}
          </span>
          {isActive && isCollapsed && (
            <div className="w-1.5 h-1.5 bg-primary-500 rounded-full flex-shrink-0 mr-1" />
          )}
          <ChevronDown className={`w-3.5 h-3.5 text-dark-500 transition-transform duration-200 flex-shrink-0 ${
            !isCollapsed ? 'rotate-180' : ''
          }`} />
        </button>

        {/* 자식 메뉴 - 부모와 동일한 레벨 */}
        <div
          className={`overflow-hidden transition-all duration-200 ${
            isCollapsed ? 'max-h-0 opacity-0' : 'max-h-[2000px] opacity-100'
          }`}
        >
          <div className="space-y-0.5">
            {group.children.map(child => renderMenuItem(child))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <aside className="h-full w-full glass border-r border-white/10 flex flex-col rounded-none" data-open={isOpen}>
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-base font-bold text-white">KRYZONIC</h1>
            <p className="text-[10px] text-dark-400">{t('sidebar:adminDashboard')}</p>
          </div>
        </div>

        {/* Mobile close button */}
        <button
          onClick={onClose}
          className="lg:hidden glass-button p-2 rounded-lg"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Navigation — 메뉴 섹션 (스크롤 영역, 하단 고정부와 분리) */}
      <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto min-h-0">
        {menuStructure.map(entry =>
          isGroup(entry) ? renderGroup(entry) : renderMenuItem(entry)
        )}
      </nav>

      {/* 실시간 접속 회원 — 하단 고정, 데스크톱 전용 */}
      <OnlineMembersMini />

      {/* 로그아웃 — 맨 하단 고정 (iOS 홈인디케이터/툴바 회피: safe-area 하단 패딩) */}
      <div className="border-t border-white/10 p-2 flex-shrink-0 pb-[calc(0.5rem+env(safe-area-inset-bottom))]">
        <button
          onClick={() => logout()}
          className="w-full flex items-center justify-center space-x-2 px-3 py-2 rounded-lg text-dark-300 hover:bg-red-500/10 hover:text-red-400 transition-colors"
        >
          <LogOut className="w-4 h-4" />
          <span className="text-sm font-medium">{t('header:logout', '로그아웃')}</span>
        </button>
      </div>
    </aside>
  )
}

export default Sidebar
